import React from "react";
import { TrendingUp, Scale, Globe2, Target, Trophy, GitBranch, Route, Bell } from "lucide-react";

const TOOLS = [
  {
    title: "Hero charts",
    desc: "High-impact, single-message visuals that carry the headline argument alone.",
    icon: TrendingUp,
    span: "md:col-span-4 lg:col-span-6",
    accent: true,
  },
  {
    title: "Meaningful comparisons",
    desc: "Peer and benchmark framing that makes the number matter.",
    icon: Scale,
    span: "md:col-span-2 lg:col-span-3",
  },
  {
    title: "Bigger-picture context",
    desc: "Global, local, economic — situate the data in a world your audience recognises.",
    icon: Globe2,
    span: "md:col-span-2 lg:col-span-3",
  },
  {
    title: "Progress against targets",
    desc: "Show distance travelled — and distance left. Hold the line accountable.",
    icon: Target,
    span: "md:col-span-2 lg:col-span-4",
  },
  {
    title: "Rankings & scoreboards",
    desc: "Competition that drives behaviour. Leaderboards people want to climb.",
    icon: Trophy,
    span: "md:col-span-2 lg:col-span-4",
    dark: true,
  },
  {
    title: "Drivers & breakdowns",
    desc: "Unpack the causes behind the headline — the anatomy of the number.",
    icon: GitBranch,
    span: "md:col-span-2 lg:col-span-4",
  },
  {
    title: "Trajectories & consequences",
    desc: "If nothing changes, here is where we land. Current-path storytelling.",
    icon: Route,
    span: "md:col-span-2 lg:col-span-6",
  },
  {
    title: "Sustained attention",
    desc: "Scheduled updates, alerts, and recurring rituals that keep the story alive.",
    icon: Bell,
    span: "md:col-span-2 lg:col-span-6",
  },
];

export default function Toolbox() {
  return (
    <section id="toolbox" data-testid="toolbox-section" className="py-24 md:py-32 bg-[#F3F4F6]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">
          <div className="max-w-2xl">
            <p className="overline mb-4">The toolbox</p>
            <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1]">
              Eight ways to make<br />
              the <span className="italic font-serif font-black">data</span> speak.
            </h2>
          </div>
          <p className="text-[#4B5563] max-w-lg leading-relaxed">
            We've sharpened techniques to achieve defined goals - whether that is to clarify, make decisions, persuade, or drive action. Our favourites include hero charts, living scoreboards, and a 'drumbeat' regular updates.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-12 gap-4 md:gap-5">
          {TOOLS.map((t, i) => {
            const Icon = t.icon;
            const dark = t.dark;
            const accent = t.accent;
            return (
              <div
                key={t.title}
                data-testid={`tool-card-${i}`}
                className={`${t.span} group relative p-6 md:p-8 transition-all duration-300 overflow-hidden
                  ${dark ? "bg-[#0A0A0A] text-white" : accent ? "bg-[#0052FF] text-white" : "bg-white text-[#0A0A0A]"}
                  border border-black/10 hover:-translate-y-1 hover:shadow-[10px_10px_0_0_rgba(10,10,10,1)]`}
              >
                <div className="flex items-start justify-between mb-8">
                  <span className={`font-mono text-xs ${dark ? "text-white/50" : accent ? "text-white/70" : "text-[#6B7280]"}`}>
                    / {String(i + 1).padStart(2, "0")}
                  </span>
                  <Icon
                    size={24}
                    strokeWidth={1.5}
                    className={dark ? "text-[#FDE047]" : accent ? "text-[#FDE047]" : "text-[#0052FF]"}
                  />
                </div>
                <h3 className="font-display font-bold text-xl md:text-2xl tracking-tight mb-3 leading-tight">
                  {t.title}
                </h3>
                <p className={`text-sm md:text-base leading-relaxed ${dark ? "text-white/70" : accent ? "text-white/85" : "text-[#4B5563]"}`}>
                  {t.desc}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
